import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, Grid, IconButton,
  InputLabel, MenuItem, Paper, Select, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon       from '@mui/icons-material/Add';
import DeleteIcon    from '@mui/icons-material/Delete';
import EditIcon      from '@mui/icons-material/Edit';
import PersonIcon    from '@mui/icons-material/Person';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import CoffeeIcon    from '@mui/icons-material/Coffee';
import InfoIcon      from '@mui/icons-material/Info';
import LocationOnIcon       from '@mui/icons-material/LocationOn';
import ExpandMoreIcon       from '@mui/icons-material/ExpandMore';
import ExpandLessIcon       from '@mui/icons-material/ExpandLess';
import Collapse             from '@mui/material/Collapse';
import { useNavigate } from 'react-router-dom';
import {
  getDevices, registerDevice, updateDevice, deleteDeviceOnly, getDeviceDependencies, cleanupDevice, getLocations,
  isAdmin, isSupervisor, isVendorAdmin, getVendorId,
  getEnrollmentPasswordStatus, setEnrollmentPassword, clearEnrollmentPassword,
} from '../api/client';
import useDetectionSettings, { enabled } from '../hooks/useDetectionSettings';

export default function FleetPage() {
  const detectionSettings = useDetectionSettings();
  const navigate = useNavigate();
  const [devices,   setDevices]   = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [success,    setSuccess]   = useState(null);

  const [regOpen,   setRegOpen]   = useState(false);
  const [regForm,   setRegForm]   = useState({ device_key: '', hostname: '', location_id: '' });
  const [regError,  setRegError]  = useState(null);
  const [regSaving, setRegSaving] = useState(false);

  const [editOpen,   setEditOpen]   = useState(false);
  const [editDevice, setEditDevice] = useState(null);
  const [editForm,   setEditForm]   = useState({ hostname: '', location_id: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError,  setEditError]  = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteDependencies, setDeleteDependencies] = useState(null);
  const [deleting,     setDeleting]     = useState(false);
  const [collapsed,    setCollapsed]    = useState({});

  // ── Enrollment password state (vendor-admin only) ────────────────
  const [enrollPwHasActive,  setEnrollPwHasActive]  = useState(false);
  const [enrollPwOpen,       setEnrollPwOpen]        = useState(false);
  const [enrollPw,           setEnrollPw]            = useState('');
  const [enrollPwConfirm,    setEnrollPwConfirm]     = useState('');
  const [enrollPwError,      setEnrollPwError]       = useState(null);
  const [enrollPwSaving,     setEnrollPwSaving]      = useState(false);
  const [enrollPwClearOpen,  setEnrollPwClearOpen]   = useState(false);
  const [enrollPwLoading,    setEnrollPwLoading]     = useState(false);

  const toggleCollapse = (locId) =>
    setCollapsed(c => ({ ...c, [locId]: !c[locId] }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const calls = [getDevices(), getLocations()];
      if (isVendorAdmin()) calls.push(getEnrollmentPasswordStatus(getVendorId()));
      const [devs, locs, pwStatus] = await Promise.all(calls);
      setDevices(devs);
      setLocations(locs);
      if (pwStatus) setEnrollPwHasActive(pwStatus.has_enrollment_password);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load devices');
    } finally { setLoading(false); }
  }, []);

  const handleSetEnrollmentPassword = async () => {
    if (enrollPw !== enrollPwConfirm) { setEnrollPwError('Passwords do not match'); return; }
    setEnrollPwSaving(true); setEnrollPwError(null);
    try {
      await setEnrollmentPassword(getVendorId(), enrollPw, enrollPwConfirm);
      setEnrollPwHasActive(true);
      setEnrollPwOpen(false);
      setEnrollPw(''); setEnrollPwConfirm('');
    } catch (e) {
      setEnrollPwError(e.response?.data?.detail || 'Failed to set enrollment password');
    } finally { setEnrollPwSaving(false); }
  };

  const handleClearEnrollmentPassword = async () => {
    setEnrollPwLoading(true);
    try {
      await clearEnrollmentPassword(getVendorId());
      setEnrollPwHasActive(false);
      setEnrollPwClearOpen(false);
    } catch (e) {
      setEnrollPwError(e.response?.data?.detail || 'Failed to clear enrollment password');
    } finally { setEnrollPwLoading(false); }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const id = setInterval(load, 30_000); return () => clearInterval(id); }, [load]);

  const handleRegister = async () => {
    setRegError(null);
    if (!regForm.device_key) { setRegError('Device key is required'); return; }
    setRegSaving(true);
    try {
      const payload = { device_key: regForm.device_key, hostname: regForm.hostname || undefined };
      if (isAdmin() && regForm.location_id) payload.location_id = regForm.location_id;
      await registerDevice(payload);
      setRegOpen(false);
      setRegForm({ device_key: '', hostname: '', location_id: '' });
      load();
    } catch (e) { setRegError(e.response?.data?.detail || 'Registration failed'); }
    finally { setRegSaving(false); }
  };

  const handleEdit = async () => {
    setEditError(null); setEditSaving(true);
    try {
      const payload = {};
      if (editForm.hostname !== undefined)    payload.hostname    = editForm.hostname;
      if (editForm.location_id !== undefined) payload.location_id = editForm.location_id || null;
      await updateDevice(editDevice.id, payload);
      setEditOpen(false); load();
    } catch (e) { setEditError(e.response?.data?.detail || 'Update failed'); }
    finally { setEditSaving(false); }
  };

  const beginDelete = async (device) => {
    setError(null); setDeleting(true);
    try {
      const preview = await getDeviceDependencies(device.id);
      setDeleteTarget(device); setDeleteDependencies(preview);
    } catch (e) { setError(e.response?.data?.detail || 'Unable to inspect device dependencies.'); }
    finally { setDeleting(false); }
  };
  const closeDelete = () => { if (!deleting) { setDeleteTarget(null); setDeleteDependencies(null); } };
  const handleDelete = async () => {
    setDeleting(true);
    try {
      if (deleteDependencies?.has_dependencies) await cleanupDevice(deleteTarget.id);
      else await deleteDeviceOnly(deleteTarget.id);
      setDeleteTarget(null); setDeleteDependencies(null); await load();
      setSuccess('Device deleted successfully.');
    } catch (e) { setError(e.response?.data?.detail || 'Device deletion failed.'); }
    finally { setDeleting(false); }
  };

  const locName = (id) => locations.find(l => l.id === id)?.name || null;

  // Group devices by location for admin view
  const grouped = React.useMemo(() => {
    if (!isAdmin()) return null;
    const map = new Map();
    map.set('__none__', { name: 'Unassigned', devices: [] });
    locations.forEach(l => map.set(l.id, { name: l.name, devices: [] }));
    devices.forEach(d => {
      const key = d.location_id && map.has(d.location_id) ? d.location_id : '__none__';
      map.get(key).devices.push(d);
    });
    // Remove empty groups (except unassigned)
    return [...map.entries()].filter(([k, v]) => k === '__none__' ? v.devices.length > 0 : true);
  }, [devices, locations]);

  const DeviceCard = ({ d }) => (
    <Paper sx={{
      p: 2, cursor: 'pointer', transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: 4 },
      borderLeft: `4px solid ${d.is_online ? '#4caf50' : '#bdbdbd'}`,
      position: 'relative',
    }} onClick={() => navigate(`/devices/${d.id}`)}>
      {isAdmin() && (
        <Box sx={{ position: 'absolute', top: 6, right: 6 }} onClick={e => e.stopPropagation()}>
          <Tooltip title="Edit / Reassign">
            <IconButton size="small" onClick={() => {
              setEditDevice(d);
              setEditForm({ hostname: d.hostname || '', location_id: d.location_id || '' });
              setEditError(null); setEditOpen(true);
            }}><EditIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            {isVendorAdmin() && <IconButton size="small" color="error" onClick={() => beginDelete(d)}>
              <DeleteIcon fontSize="small" />
            </IconButton>}
          </Tooltip>
        </Box>
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start"
        sx={{ pr: isAdmin() ? 7 : 0 }}>
        <Typography variant="subtitle2" fontWeight={700} noWrap>
          {d.hostname || d.id.slice(0, 12)}
        </Typography>
        <Chip label={d.is_online ? 'Online' : 'Offline'}
          color={d.is_online ? 'success' : 'default'} size="small" />
      </Stack>

      <Box sx={{ mt: 1, p: 1, bgcolor: d.current_operator ? '#f3f8ff' : '#fafafa',
                 borderRadius: 1, border: '1px solid #e0e0e0' }}>
        {d.current_operator ? (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {d.current_operator.status === 'break'
              ? <CoffeeIcon sx={{ fontSize: 16, color: '#ff9800' }} />
              : <PersonIcon sx={{ fontSize: 16, color: '#1976d2' }} />}
            <Box>
              <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
                {d.current_operator.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                #{d.current_operator.employee_id}
                {d.current_operator.status === 'break' && ' · On Break'}
              </Typography>
            </Box>
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <PersonOffIcon sx={{ fontSize: 16, color: '#9e9e9e' }} />
            <Typography variant="body2" color="text.secondary" fontSize="0.75rem">No operator</Typography>
          </Stack>
        )}
      </Box>

      <Stack direction="row" spacing={1} mt={1}>
        {enabled(detectionSettings, 'drowsiness') && <Chip label={`😴 ${d.drowsiness_count}`} size="small" variant="outlined"
          color={d.drowsiness_count > 0 ? 'warning' : 'default'} />
        }
        {enabled(detectionSettings, 'yawning') &&
          <Chip label={`🥱 ${d.yawn_count}`} size="small" variant="outlined" />}
        {enabled(detectionSettings, 'distracted') &&
          <Chip label={`👀 ${d.distracted_count ?? 0}`} size="small" variant="outlined"
            sx={{ borderColor: '#9c27b0', color: (d.distracted_count > 0) ? '#9c27b0' : 'text.secondary' }} />}
      </Stack>
      {enabled(detectionSettings, 'presence') && d.presence && (
        <Chip size="small" sx={{ mt: 1, bgcolor: '#e0f2f1', color: '#00695c' }}
          label={`Presence Monitoring: ${d.presence.current_status?.replace('_', ' ')} · ${d.presence.presence_percent}%`} />
      )}
      {d.is_online && (
        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
          CPU {d.cpu_percent?.toFixed(0) ?? '—'}% · {d.fps?.toFixed(1) ?? '—'} fps
        </Typography>
      )}
    </Paper>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>
          Fleet
          <Typography component="span" variant="body2" color="text.secondary" ml={1}>
            {devices.length} device{devices.length !== 1 ? 's' : ''}
          </Typography>
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {/* Enrollment password controls — vendor-admin only */}
          {isVendorAdmin() && (
            <Stack direction="row" spacing={1} alignItems="center"
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1.5, py: 0.5 }}>
              <Chip
                label={enrollPwHasActive ? 'Enrollment Password: Set' : 'Enrollment Password: None'}
                size="small"
                color={enrollPwHasActive ? 'success' : 'default'}
                variant="outlined"
              />
              <Button size="small" variant="outlined"
                onClick={() => { setEnrollPwError(null); setEnrollPw(''); setEnrollPwConfirm(''); setEnrollPwOpen(true); }}
                disabled={enrollPwLoading}>
                {enrollPwHasActive ? 'Change Password' : 'Set Password'}
              </Button>
              {enrollPwHasActive && (
                <Button size="small" color="error" variant="outlined"
                  onClick={() => setEnrollPwClearOpen(true)} disabled={enrollPwLoading}>
                  Clear
                </Button>
              )}
            </Stack>
          )}
          {enrollPwError && !enrollPwOpen && (
            <Typography variant="caption" color="error">{enrollPwError}</Typography>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setRegOpen(true)}>
            Register Device
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
      {isSupervisor() && (
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
          Showing devices in your location. Contact admin to assign devices to your location.
        </Alert>
      )}

      {loading && devices.length === 0 ? (
        <Box sx={{ textAlign: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : isAdmin() && grouped ? (
        // Admin: grouped by location
        grouped.map(([locId, group]) => (
          <Box key={locId} mb={3}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}
              sx={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => toggleCollapse(locId)}>
              <LocationOnIcon color={locId === '__none__' ? 'warning' : 'primary'} />
              <Typography variant="h6" fontWeight={600}
                color={locId === '__none__' ? 'warning.main' : 'text.primary'}>
                {group.name}
              </Typography>
              <Chip label={`${group.devices.length} device${group.devices.length !== 1 ? 's' : ''}`}
                size="small" variant="outlined" />
              {locId === '__none__' && (
                <Typography variant="caption" color="warning.main">
                  · Supervisors won't see these until assigned
                </Typography>
              )}
              <Box sx={{ flexGrow: 1 }} />
              {collapsed[locId] ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </Stack>
            <Collapse in={!collapsed[locId]}>
              {group.devices.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  No devices assigned to this location yet.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {group.devices.map(d => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={d.id}>
                      <DeviceCard d={d} />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Collapse>
            <Divider sx={{ mt: 2 }} />
          </Box>
        ))
      ) : (
        // Supervisor: flat grid
        <Grid container spacing={2}>
          {devices.map(d => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={d.id}>
              <DeviceCard d={d} />
            </Grid>
          ))}
          {devices.length === 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 6, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No devices assigned to your location yet. Ask admin to assign devices.
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* Register dialog */}
      <Dialog open={regOpen} onClose={() => setRegOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Register Device</DialogTitle>
        <DialogContent>
          {regError && <Alert severity="error" sx={{ mb: 2 }}>{regError}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Device Key" required size="small" value={regForm.device_key}
              onChange={e => setRegForm(f => ({ ...f, device_key: e.target.value }))}
              helperText="Key configured on the edge device" />
            <TextField label="Hostname (optional)" size="small" value={regForm.hostname}
              onChange={e => setRegForm(f => ({ ...f, hostname: e.target.value }))} />
            {isAdmin() && (
              <FormControl size="small">
                <InputLabel>Location</InputLabel>
                <Select value={regForm.location_id} label="Location"
                  onChange={e => setRegForm(f => ({ ...f, location_id: e.target.value }))}>
                  <MenuItem value="">None</MenuItem>
                  {locations.map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            {isSupervisor() && (
              <Alert severity="info" sx={{ py: 0.5 }}>Auto-assigned to your location.</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegOpen(false)}>Cancel</Button>
          <Button onClick={handleRegister} variant="contained" disabled={regSaving}>
            {regSaving ? <CircularProgress size={20} /> : 'Register'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Device — {editDevice?.hostname || editDevice?.id?.slice(0, 12)}</DialogTitle>
        <DialogContent>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Hostname" size="small" value={editForm.hostname}
              onChange={e => setEditForm(f => ({ ...f, hostname: e.target.value }))} />
            <FormControl size="small">
              <InputLabel>Location</InputLabel>
              <Select value={editForm.location_id} label="Location"
                onChange={e => setEditForm(f => ({ ...f, location_id: e.target.value }))}>
                <MenuItem value="">Unassigned</MenuItem>
                {locations.map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={handleEdit} variant="contained" disabled={editSaving}>
            {editSaving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onClose={closeDelete} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Device?</DialogTitle>
        <DialogContent>
          <Typography>Delete <strong>{deleteTarget?.hostname || deleteTarget?.id?.slice(0, 12)}</strong>?</Typography>
          {deleteDependencies && (() => {
            const reasons = deleteDependencies.reasons || [];
            return <Box sx={{ mt: 2 }}>
              {reasons.length > 0 && <>
                <Typography variant="subtitle2">Cannot delete cleanly because:</Typography>
                <Box component="ul" sx={{ mt: 0.5, mb: 1, pl: 3 }}>
                  {reasons.map((r, i) => <li key={i}><Typography variant="body2">{r}</Typography></li>)}
                </Box>
              </>}
              {deleteDependencies.has_active_sessions &&
                <Alert severity="error" sx={{ mt: 1 }}>This device has active operator session(s). Deleting it will forcibly end them.</Alert>}
              {!deleteDependencies.has_dependencies
                ? <Alert severity="info" sx={{ mt: 1 }}>This device has no dependent records.</Alert>
                : <Alert severity="warning" sx={{ mt: 1 }}>This operation is irreversible. Associated data will be permanently deleted.</Alert>}
            </Box>;
          })()}
          <Typography variant="body2" color="text.secondary" mt={1}>
            Cannot be undone. Ensure no operator is currently logged in.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelete} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting || !deleteDependencies}>
            {deleting ? <CircularProgress size={20} /> : deleteDependencies?.has_dependencies ? 'Delete Device and Associated Data' : 'Delete Device'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Set / change enrollment password */}
      <Dialog open={enrollPwOpen} onClose={() => setEnrollPwOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {enrollPwHasActive ? 'Change Enrollment Password' : 'Set Enrollment Password'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Operators type this password into the edge client's enrollment screen on first run.
            You can change it at any time — existing registered devices are unaffected.
          </Typography>
          {enrollPwError && (
            <Alert severity="error" sx={{ mb: 2 }}>{enrollPwError}</Alert>
          )}
          <TextField
            label="New enrollment password" type="password" fullWidth size="small"
            value={enrollPw} onChange={e => setEnrollPw(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Confirm password" type="password" fullWidth size="small"
            value={enrollPwConfirm} onChange={e => setEnrollPwConfirm(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSetEnrollmentPassword(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnrollPwOpen(false)}>Cancel</Button>
          <Button onClick={handleSetEnrollmentPassword} variant="contained" disabled={enrollPwSaving}>
            {enrollPwSaving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear enrollment password — confirmation */}
      <Dialog open={enrollPwClearOpen} onClose={() => setEnrollPwClearOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Clear Enrollment Password?</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1 }}>
            Existing devices keep working — they use their own <strong>device_key</strong>,
            not this password. Only new device self-registrations will be blocked until
            you set a new enrollment password.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnrollPwClearOpen(false)}>Cancel</Button>
          <Button onClick={handleClearEnrollmentPassword} color="error" variant="contained" disabled={enrollPwLoading}>
            {enrollPwLoading ? <CircularProgress size={20} /> : 'Clear Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
